"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useKaiGame } from "@datagutt/kai-next/game";
import { useMenuKeys } from "@datagutt/kai-next/menu-keys";
import { credits } from "@/content/credits";

/** Where the title screen is: the "Press start" splash, its menu, or a page off the menu. */
type Screen = "splash" | "menu" | "confirm" | "credits";
const loadGame = () => import("@/game").then(game => game.startFjordTown);
const JOURNAL_LABEL = "Read it as a normal website";

/**
 * Hosts the game canvas behind the server-rendered title screen (docs/PLAN.md M5.11).
 * The game module starts downloading as soon as the page is interactive. "Press start"
 * opens the menu straight away; picking a way in waits for loading to finish.
 */
export function GameShell(t0) {
  const $ = _c(55);
  const {
    titleArt
  } = t0;
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [screen, setScreen] = useState("splash");
  const {
    containerRef,
    phase,
    progress,
    hasSave,
    start,
    playTitleMusic
  } = useKaiGame(loadGame);
  const playing = phase === "playing";
  const t1 = !playing;
  const t2 = screen === "splash";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setScreen("menu");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setScreen(_temp);
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t1 || $[3] !== t2) {
    t5 = {
      enabled: t1,
      splash: t2,
      menuRef,
      openMenu: t3,
      back: t4
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  useMenuKeys(t5);
  let t6;
  if ($[5] !== screen) {
    t6 = () => {
      const menu = menuRef.current;
      if (screen === "splash" || !menu || menu.contains(document.activeElement)) {
        return;
      }
      menu.querySelector("[data-menu-item]")?.focus();
    };
    $[5] = screen;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== hasSave || $[8] !== screen) {
    t7 = [screen, hasSave];
    $[7] = hasSave;
    $[8] = screen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] !== phase || $[11] !== playTitleMusic || $[12] !== screen) {
    t8 = () => {
      if (screen !== "splash" && phase !== "playing") {
        playTitleMusic();
      }
    };
    t9 = [screen, phase, playTitleMusic];
    $[10] = phase;
    $[11] = playTitleMusic;
    $[12] = screen;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[15] !== playing) {
    t10 = () => {
      const root = rootRef.current;
      if (playing || !root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      const onMove = e => {
        if (e.pointerType !== "mouse") {
          return;
        }
        root.style.setProperty("--title-x", ((0.5 - e.clientX / window.innerWidth) * 2).toFixed(3));
        root.style.setProperty("--title-y", ((0.5 - e.clientY / window.innerHeight) * 2).toFixed(3));
      };
      window.addEventListener("pointermove", onMove);
      return () => window.removeEventListener("pointermove", onMove);
    };
    t11 = [playing];
    $[15] = playing;
    $[16] = t10;
    $[17] = t11;
  } else {
    t10 = $[16];
    t11 = $[17];
  }
  useEffect(t10, t11);
  let t12;
  if ($[18] !== start) {
    t12 = fresh => start(fresh);
    $[18] = start;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const enter = t12;
  const ready = phase === "ready";
  let t13;
  if ($[20] !== phase || $[21] !== progress) {
    t13 = phase === "failed" ? "Could not load" : `Loading ${Math.round(progress * 100)}%`;
    $[20] = phase;
    $[21] = progress;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const loadingLabel = t13;
  let t14;
  if ($[23] !== containerRef) {
    t14 = <div ref={containerRef} tabIndex={-1} className="absolute inset-0 outline-none" role="application" aria-label="Fjord Town, a game version of datagutt's portfolio. The Journal link after it has the same content as a normal web page." />;
    $[23] = containerRef;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = `z-20 border-2 border-[#e8f5e9]/70 bg-[#0b1320]/80 px-3 py-1.5 font-pixel text-xs uppercase tracking-wider text-[#e8f5e9] ${playing ? "absolute right-3 top-3 md:sr-only md:focus:not-sr-only" : "sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3"}`;
  let t16;
  if ($[25] !== t15) {
    t16 = <Link href="/journal" aria-label="Journal: read it as a normal website" className={t15}>Journal</Link>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = `absolute inset-0 transition-opacity duration-700 ${playing ? "pointer-events-none opacity-0" : "opacity-100"}`;
  let t18;
  if ($[27] !== screen) {
    t18 = screen === "splash" ? () => setScreen("menu") : undefined;
    $[27] = screen;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="absolute inset-x-0 top-[9vh] flex flex-col items-center px-4 text-center"><h1 className="title-logo font-pixel text-6xl uppercase tracking-[0.12em] text-[#fff4d6] sm:text-8xl">datagutt</h1><p className="mt-4 border-2 border-[#1b2440] bg-[#b83a38] px-4 py-1 font-pixel text-base uppercase tracking-[0.3em] text-[#fff4d6] shadow-[3px_3px_0_#1b2440] sm:text-lg">Fjord Town</p></div>;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== screen) {
    t20 = screen === "splash" && <button type="button" onClick={e_0 => {
      e_0.stopPropagation();
      setScreen("menu");
    }} className="title-blink font-pixel text-xl uppercase tracking-[0.25em] text-[#fff4d6] [text-shadow:2px_2px_0_#1b2440,-2px_-2px_0_#1b2440,2px_-2px_0_#1b2440,-2px_2px_0_#1b2440,4px_4px_0_#1b2440] sm:text-3xl">Press start</button>;
    $[30] = screen;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== enter || $[33] !== hasSave || $[34] !== loadingLabel || $[35] !== ready || $[36] !== screen) {
    t21 = screen !== "splash" && <div ref={menuRef} className="w-full max-w-sm border-2 border-[#fff4d6] bg-[#1b2440]/90 p-3 shadow-[4px_4px_0_#0b1320]">{screen === "menu" && <nav aria-label="Title menu"><ul className="flex flex-col">{hasSave && <MenuItem disabled={!ready} onClick={() => enter(false)}>{ready ? "Continue" : loadingLabel}</MenuItem>}<MenuItem disabled={!ready} onClick={() => hasSave ? setScreen("confirm") : enter(false)}>{ready || hasSave ? "New game" : loadingLabel}</MenuItem><MenuItem onClick={() => setScreen("credits")}>Credits</MenuItem><MenuItem href="/journal">{JOURNAL_LABEL}</MenuItem></ul></nav>}{screen === "confirm" && <div role="alertdialog" aria-labelledby="title-confirm" className="flex flex-col gap-3"><p id="title-confirm" className="px-2 pt-1 font-pixel text-sm leading-relaxed text-[#fff4d6]">Start a new game? Your passport stamps and progress will be forgotten.</p><ul className="flex flex-col"><MenuItem onClick={() => setScreen("menu")}>Keep my save</MenuItem><MenuItem onClick={() => enter(true)}>Start over</MenuItem></ul></div>}{screen === "credits" && <div className="flex flex-col gap-2"><div className="max-h-[40vh] overflow-y-auto px-2 pt-1 text-center font-pixel text-xs leading-relaxed text-[#fff4d6]"><p className="text-sm text-[#7ee0a8]">{credits.title}</p><p>{credits.byline}</p>{credits.sections.map(_temp3)}<p className="mt-3 text-[#7ee0a8]">{credits.thanks}</p></div><ul className="flex flex-col"><MenuItem onClick={() => setScreen("menu")}>Back</MenuItem></ul></div>}</div>;
    $[32] = enter;
    $[33] = hasSave;
    $[34] = loadingLabel;
    $[35] = ready;
    $[36] = screen;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== phase) {
    t22 = phase === "failed" && <p className="mt-4 max-w-sm bg-[#1b2440]/90 p-3 font-sans text-sm text-[#fff4d6]">The game could not start in this browser. Everything is on the <Link href="/journal" className="underline">normal website</Link>.</p>;
    $[38] = phase;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <noscript><p className="mt-4 max-w-sm bg-[#1b2440]/90 p-3 font-sans text-sm text-[#fff4d6]">The game needs JavaScript. Everything is on the <a href="/journal" className="underline">normal website</a>.</p></noscript>;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t20 || $[42] !== t21 || $[43] !== t22) {
    t24 = <div className="absolute inset-x-0 bottom-[12vh] flex flex-col items-center px-4">{t20}{t21}{t22}{t23}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== playing || $[46] !== t17 || $[47] !== t18 || $[48] !== t24 || $[49] !== titleArt) {
    t25 = <div className={t17} aria-hidden={playing} inert={playing} onClick={t18}>{titleArt}{t19}{t24}</div>;
    $[45] = playing;
    $[46] = t17;
    $[47] = t18;
    $[48] = t24;
    $[49] = titleArt;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t14 || $[52] !== t16 || $[53] !== t25) {
    t26 = <div ref={rootRef} className="fixed inset-0 overflow-hidden bg-[#3f7fe0]">{t14}{t16}{t25}</div>;
    $[51] = t14;
    $[52] = t16;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  return t26;
}

/**
 * One line of the menu, with a pixel cursor while it is hovered or focused. A disabled
 * line (still loading) stays focusable, so the cursor can wait on it until it works.
 */
function _temp3(section) {
  return <div key={section.heading} className="mt-3"><p className="text-[#7ee0a8]">{section.heading}</p>{section.lines.map(_temp2)}</div>;
}
function _temp2(line) {
  return <p key={line}>{line}</p>;
}
function _temp(current) {
  return current === "menu" ? "splash" : "menu";
}
function MenuItem(t0) {
  const $ = _c(6);
  const {
    children,
    onClick,
    href,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden="true" className="w-4 text-[#ffd166] opacity-0 group-hover:opacity-100 group-focus:opacity-100">▶</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cursor = t1;
  let t2;
  if ($[1] !== children || $[2] !== disabled || $[3] !== href || $[4] !== onClick) {
    t2 = <li>{href ? <Link href={href} data-menu-item={true} className="group flex w-full items-center gap-2 px-2 py-2 text-left font-pixel text-base uppercase tracking-wider text-[#fff4d6] outline-none hover:bg-[#fff4d6]/10 focus-visible:bg-[#fff4d6]/10 aria-disabled:cursor-wait aria-disabled:opacity-60 sm:text-lg">{cursor}{children}</Link> : <button type="button" data-menu-item={true} onClick={disabled ? undefined : onClick} aria-disabled={disabled || undefined} className="group flex w-full items-center gap-2 px-2 py-2 text-left font-pixel text-base uppercase tracking-wider text-[#fff4d6] outline-none hover:bg-[#fff4d6]/10 focus-visible:bg-[#fff4d6]/10 aria-disabled:cursor-wait aria-disabled:opacity-60 sm:text-lg">{cursor}{children}</button>}</li>;
    $[1] = children;
    $[2] = disabled;
    $[3] = href;
    $[4] = onClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}